import axios from "axios";
import { getSourceFilePathObj, wait } from "~/utils/miscellaneous";

const sharedLibMockContext = require.context(
  "~/assets/mock",
  true,
  /.*\.json$/
);

let lastIntanceId = 0;

function triggerInterceptor(data, isError) {
  let handlers = axios.interceptors[this.interceptorType].handlers;

  if (this.interceptorType == "request") handlers = handlers.slice().reverse();

  handlers.forEach((handler) => {
    try {
      const { fulfilled, rejected } = handler;
      if (isError) {
        if (rejected) {
          data = rejected(data);
          isError = false;
        }
      } else {
        if (fulfilled) {
          data = fulfilled(data);
          isError = false;
        }
      }
    } catch (e) {
      data = e;
      isError = true;
    }
  });

  if (isError) throw data;

  return data;
}

axios.interceptors.request.trigger = triggerInterceptor.bind({
  interceptorType: "request",
});
axios.interceptors.response.trigger = triggerInterceptor.bind({
  interceptorType: "response",
});

export default class MyAxios extends axios.Axios {
  constructor(mockContext, isMockEnabled, requestConfigAppGlobals, backendName) {
    super(axios.defaults);
    this.interceptors = axios.interceptors;

    this.__instanceId = ++lastIntanceId;
    this.__lastRequestId = 0;

    this.__mockContext = mockContext;
    this._isMockEnabled = isMockEnabled;
    this.__requestConfigAppGlobals = requestConfigAppGlobals;
    this.__backendName = backendName;
  }

  setAllRequestConfig(config) {
    config.requestId = this.__instanceId + "-" + ++this.__lastRequestId;
    config.appGlobals = this.__requestConfigAppGlobals;
    config.backendName = this.__backendName;
    config.returnFullResponse = config.returnFullResponse ?? false;

    return config;
  }

  request(config) {
    this.setAllRequestConfig(config);

    this.onBeforeRequest(config);

    return super.request(config).then(
      (res) => this.__onRequestComplete(false, config, res),
      (err) => this.__onRequestComplete(true, config, err)
    );
  }

  checkReturnFullResponse(isError, config, data) {
    return config.returnFullResponse ? data : data.data;
  }

  __onRequestComplete(isError, config, data) {
    this.onRequestComplete(isError, config, data);
    const res = this.checkReturnFullResponse(isError, config, data);
    if (isError) return Promise.reject(res);
    return res;
  }

  async getMock(mockSourceFilePath, payload, config) {
    let requestConfig = this.setAllRequestConfig({
      isMock: true,
      data: JSON.stringify(payload),
      ...config,
    });

    const sourceFilePathObj = getSourceFilePathObj(
      mockSourceFilePath
    );

    Object.assign(requestConfig, {
      mockSource: sourceFilePathObj.source,
      mockFilePath: sourceFilePathObj.filePath,
      url: sourceFilePathObj.filePath,
    });

    let response;
    let isError;

    this.onBeforeRequest(requestConfig);

    requestConfig = this.interceptors.request.trigger(requestConfig);

    await wait(2000);

    try {
      const mockRequireFilePath = `./${requestConfig.mockFilePath}.json`;
      let mockData = Object.assign(
        {},
        requestConfig.mockSource == "shared-lib"
          ? sharedLibMockContext(mockRequireFilePath)
          : this.__mockContext(mockRequireFilePath)
      );

      isError = false;
      response = {
        request: {
          responseText: JSON.stringify(mockData),
        },
        config: requestConfig,
        data: mockData,
        status: 200,
        statusText: "OK",
      };
    } catch (error) {
      let statusCode = error.code == "MODULE_NOT_FOUND" ? 404 : error.code;
      let statusText =
        error.code == "MODULE_NOT_FOUND" ? "Not Found" : error.message;

      isError = true;
      response = Error(`Request failed with status code ${statusCode}`);
      Object.assign(response, {
        config: requestConfig,
        isAxiosError: true,
        response: {
          request: {
            responseText: "{}",
          },
          config: requestConfig,
          data: {},
          status: statusCode,
          statusText: statusText,
        },
      });
    }

    try {
      response = this.interceptors.response.trigger(response, isError);
      response = this.__onRequestComplete(false, requestConfig, response);
    } catch (e) {
      response = this.__onRequestComplete(true, requestConfig, e);
    }

    return response;
  }
  onBeforeRequest() {
    //child class can override this function
  }
  onRequestComplete() {
    //child class can override this function
  }
}
